import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
    static targets = ["dialog", "dialogTitle", "dialogVideoFrame"];

    declare readonly dialogTarget: HTMLDialogElement;
    declare readonly dialogTitleTarget: HTMLElement;
    declare readonly dialogVideoFrameTarget: HTMLElement;

    private dialogMedia: HTMLVideoElement | HTMLImageElement | null = null;

    open(event: Event): void {
        const trigger = event.currentTarget;

        if (!(trigger instanceof HTMLButtonElement)) {
            return;
        }

        this.showPreview(trigger.querySelector("video"), trigger.querySelector("img"));
    }

    openFromMarkdown(event: Event): void {
        const previewImage = event.target;
        if (
            !(previewImage instanceof HTMLImageElement) ||
            !previewImage.closest(".ticket-chat-markdown") ||
            previewImage.closest("a")
        ) {
            return;
        }

        if (event instanceof KeyboardEvent) {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
        }

        this.showPreview(null, previewImage);
    }

    private showPreview(
        previewVideo: HTMLVideoElement | null,
        previewImage: HTMLImageElement | null,
    ): void {
        const source = previewVideo?.querySelector("source");
        let dialogMedia: HTMLVideoElement | HTMLImageElement;
        let label = this.dialogTitleTarget.textContent?.trim() || "";

        if (previewVideo instanceof HTMLVideoElement && source instanceof HTMLSourceElement) {
            const dialogVideo = document.createElement("video");
            label = previewVideo.getAttribute("aria-label") || label;
            dialogVideo.controls = true;
            dialogVideo.loop = true;
            dialogVideo.playsInline = true;
            dialogVideo.preload = "metadata";
            dialogVideo.src = source.src;
            dialogVideo.poster = previewVideo.poster;
            dialogMedia = dialogVideo;
        } else if (previewImage instanceof HTMLImageElement) {
            const dialogImage = document.createElement("img");
            label = previewImage.alt || label;
            dialogImage.src = previewImage.currentSrc || previewImage.src;
            dialogImage.alt = label;
            dialogMedia = dialogImage;
        } else {
            return;
        }

        dialogMedia.className = "block max-h-[calc(100dvh-2rem)] w-full rounded-2xl object-contain";
        dialogMedia.setAttribute("aria-label", label);
        dialogMedia.setAttribute("data-testid", "changelog-video-dialog-player");

        this.dialogTitleTarget.textContent = label;
        this.dialogVideoFrameTarget.replaceChildren(dialogMedia);
        this.dialogMedia = dialogMedia;

        if (!this.dialogTarget.open) {
            this.dialogTarget.showModal();
        }

        if (dialogMedia instanceof HTMLVideoElement) {
            void dialogMedia.play().catch(() => undefined);
        }
    }

    close(event: Event): void {
        event.preventDefault();
        this.dialogTarget.close();
    }

    closeFromBackdrop(event: MouseEvent): void {
        if (event.target === this.dialogTarget) {
            this.dialogTarget.close();
        }
    }

    cleanup(): void {
        if (this.dialogMedia instanceof HTMLVideoElement) {
            this.dialogMedia.pause();
        }
        this.dialogVideoFrameTarget.replaceChildren();
        this.dialogMedia = null;
    }
}
